/**
 * ============================================================================
 *  File        : frontend/src/api/reportsHub.ts
 *  Module      : api
 *  Purpose     : 報表中心（Reports hub）唯讀資料 GET — 6 份報表（spec §8.2 /reports）
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler（掛在 v1，RBAC 見各 fn）：
 *    GET /api/v1/reports/cases-progress        -> v13_reports::cases_progress
 *    GET /api/v1/reports/weight-reconciliation -> v13_reports::weight_reconciliation
 *    GET /api/v1/reports/settlements           -> v13_reports::settlements
 *    GET /api/v1/reports/auctions              -> v13_reports::auctions
 *    GET /api/v1/reports/freee-sla             -> v13_reports::freee_sla
 *    GET /api/v1/reports/audit                 -> v13_reports::audit（額外需 system.audit.view）
 *
 *  共通：回 { data: [...], meta: { page, per_page, total } };Decimal 一律「字串」
 *  收送（前端不得轉 float,CLAUDE.md §2);datetime 為 RFC3339。query 參數
 *  from / to / status / case_no / page / per_page（各端點對 status/case_no 的語意見
 *  generated schema 註解;這裡統一透傳)。型別取自 generated schema.d.ts。
 *
 *  ⚠️ 與既有 `api/reports.ts`（最終重量 / 最終報告 / 三方確認）不衝突 — 此模組
 *  專供 /reports 報表中心頁,故另起 `reportsHub` 名稱。
 */

import { API_BASE, requestEnvelope, type ApiSchemas, type Envelope } from './client'

const REPORTS_PATH = `${API_BASE}/reports`

/** 報表共通 query 參數（透傳給後端;undefined 不送出）。 */
export interface ReportQuery {
  from?: string
  to?: string
  status?: string
  caseNo?: string
  page?: number
  perPage?: number
}

/** ① 案件進度報表列（= 後端 CaseProgressRowDto）。 */
export type CaseProgressRow = ApiSchemas['CaseProgressRowDto']
/** ② 重量差異報表列（= 後端 WeightReconciliationRowDto）。 */
export type WeightReconciliationRow = ApiSchemas['WeightReconciliationRowDto']
/** ③ 精算報表列（per case;P1/P2/P3 折成一列;= 後端 SettlementSummaryRowDto）。 */
export type SettlementSummaryRow = ApiSchemas['SettlementSummaryRowDto']
/** 精算各回 phase 摘要（p1/p2/p3 共用形狀,欄位皆 optional）。 */
export type SettlementPhaseSummary = ApiSchemas['SettlementPhaseSummaryDto']
/** ④ 入札報表列（= 後端 AuctionSummaryRowDto）。 */
export type AuctionSummaryRow = ApiSchemas['AuctionSummaryRowDto']
/** 落札結果（= 後端 AwardOutcomeDto）。 */
export type AwardOutcome = ApiSchemas['AwardOutcomeDto']
/** ⑤ Freee SLA 報表列（= 後端 FreeeSlaRowDto）。 */
export type FreeeSlaRow = ApiSchemas['FreeeSlaRowDto']
/** ⑥ Audit 報表列（= 後端 AuditRowDto）。 */
export type AuditRow = ApiSchemas['AuditRowDto']

/** 把共通 query 物件轉成 URLSearchParams（只送有值的鍵）。 */
function toSearchParams(query: ReportQuery): string {
  const params = new URLSearchParams()
  if (query.from) params.set('from', query.from)
  if (query.to) params.set('to', query.to)
  if (query.status) params.set('status', query.status)
  if (query.caseNo) params.set('case_no', query.caseNo)
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.perPage !== undefined) params.set('per_page', String(query.perPage))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** 共通 GET：回 { data, meta } envelope。 */
function getReport<T>(path: string, query: ReportQuery): Promise<Envelope<T[]>> {
  return requestEnvelope<T[]>(`${REPORTS_PATH}/${path}${toSearchParams(query)}`, {
    method: 'GET',
  })
}

/** ① 案件進度報表（RBAC：marketplace.settlements.view 或 all-access）。 */
export function getCasesProgressReport(
  query: ReportQuery = {},
): Promise<Envelope<CaseProgressRow[]>> {
  return getReport<CaseProgressRow>('cases-progress', query)
}

/** ② 重量差異報表。 */
export function getWeightReconciliationReport(
  query: ReportQuery = {},
): Promise<Envelope<WeightReconciliationRow[]>> {
  return getReport<WeightReconciliationRow>('weight-reconciliation', query)
}

/** ③ 精算報表（P1/P2/P3 折成一列）。 */
export function getSettlementsReport(
  query: ReportQuery = {},
): Promise<Envelope<SettlementSummaryRow[]>> {
  return getReport<SettlementSummaryRow>('settlements', query)
}

/** ④ 入札報表。 */
export function getAuctionsReport(
  query: ReportQuery = {},
): Promise<Envelope<AuctionSummaryRow[]>> {
  return getReport<AuctionSummaryRow>('auctions', query)
}

/** ⑤ Freee SLA 報表。 */
export function getFreeeSlaReport(query: ReportQuery = {}): Promise<Envelope<FreeeSlaRow[]>> {
  return getReport<FreeeSlaRow>('freee-sla', query)
}

/** ⑥ Audit 報表（額外需 system.audit.view）。 */
export function getAuditReport(query: ReportQuery = {}): Promise<Envelope<AuditRow[]>> {
  return getReport<AuditRow>('audit', query)
}
